import {
  AttachmentObjectDescription,
  BiterampObjectDescription,
  ElasticObjectDescription,
  GingivaObjectDescription,
  ICbct,
  IIPR,
  IJaw,
  IOcclusionFeatureConfig,
  IPRObjectDescription,
  IStage,
  IToothlikeOcclusion,
  JawType,
  ObjectDescription,
  PonticObjectDescription,
  ToothObjectDescription
} from '@data/models';
import {Dictionary} from '@types';
import {cloneDeep} from 'lodash';
import {getNameByFile} from './getNameByFile';
import {
  getOcclusionDistancesFromFilename,
} from '@data/providers/SceneDataProvider/utils/getOcclusions';
import {RawFile} from '@data/providers/SceneDataProvider/types';
import {ConfigProvider} from '@data/providers';


const fillJawObjects = async (
  jaw: IJaw,
  objects: Dictionary<ObjectDescription>,
  jawType: JawType,
  stageId: string,
  fileNames: string[],
  occlusions: RawFile[],
  configProvider?: ConfigProvider,
  cbct?: ICbct,
  stageJawDisplacement?: any
) => {
  const gingivaName = getNameByFile(fileNames[jaw.gingiva.file]);
  const gingivaObject = objects[gingivaName] as GingivaObjectDescription;
  gingivaObject.visible = true;
  gingivaObject.transform = jaw.gingiva.transform;
  gingivaObject.jaw = jawType;
  gingivaObject.jawDisplacementTransform = stageJawDisplacement?.transform || null;

  const occlusionsConfig = configProvider?.getFeatureConfig<IOcclusionFeatureConfig>("occlusions")
  const cbctJawFilename = jawType === JawType.Upper ? cbct?.Upper : cbct?.Lower;
  if (cbctJawFilename) {
    const cbctJawName = getNameByFile(fileNames[cbctJawFilename!]);
    const cbctJawObject = objects[cbctJawName];
    cbctJawObject.visible = true;
    cbctJawObject.transform = jaw.gingiva.transform;
    cbctJawObject.jaw = jawType;
  }

  for (const tooth of jaw.teeth) {
    const toothName = getNameByFile(fileNames[tooth.file]);
    const toothObject = objects[toothName] as ToothObjectDescription;
    toothObject.visible = true;
    toothObject.transform = tooth.transform;
    toothObject.jaw = jawType;

    // Handle regular occlusion
    if (tooth.occlusion) {
      toothObject.occlusionDistances = await getOcclusionDistancesFromFilename(tooth.occlusion, occlusions, occlusionsConfig || undefined);
    }

    // Handle jaw displacement occlusion
    if (stageJawDisplacement?.occlusions) {
      const jdOcclusion = stageJawDisplacement.occlusions.find((occ: IToothlikeOcclusion) => occ.toothlikeId === tooth.id);
      if (jdOcclusion) {
        toothObject.jdOcclusionDistances = await getOcclusionDistancesFromFilename(jdOcclusion.occlusion, occlusions, occlusionsConfig || undefined);
      }
    }

    const rootName = toothName.replace('tooth-', 'root-');
    const rootObject = objects[rootName];
    if (rootObject) {
      rootObject.visible = true;
      rootObject.transform = tooth.transform;
      rootObject.jaw = jawType;
    }
  }

  if (jaw.attachments) {
    jaw.attachments.forEach((attachment: any) => {
      const attachmentName = getNameByFile(fileNames[attachment.file]);
      const attachmentObject = objects[attachmentName];
      attachmentObject.visible = true;
      attachmentObject.transform = attachment.transform;
      attachmentObject.jaw = jawType;
      (attachmentObject as AttachmentObjectDescription).toothId = attachment.number;
    });
  }

  if (jaw.elastics) {
    jaw.elastics.forEach((elastic) => {
      const elasticName = `${elastic.id}--${stageId}`;
      const elasticObject = objects[elasticName];
      elasticObject.visible = true;
      elasticObject.jaw = jawType;
      (elasticObject as ElasticObjectDescription).toothId = elastic.number;
    });
  }

  if (jaw.biteRamps) {
    jaw.biteRamps.forEach((biteRamp: any) => {
      const biterampName = getNameByFile(fileNames[biteRamp.file]);
      const biterampObject = objects[biterampName];
      biterampObject.visible = true;
      biterampObject.transform = biteRamp.transform;
      biterampObject.jaw = jawType;
      (biterampObject as BiterampObjectDescription).toothId = biteRamp.number;
    });
  }

  if (jaw.pontics) {
    jaw.pontics.forEach(async (pontic: any) => {
      const ponticName = getNameByFile(fileNames[pontic.file]);
      const ponticObject = objects[ponticName] as PonticObjectDescription;
      ponticObject.visible = true;
      ponticObject.transform = pontic.transform;
      ponticObject.jaw = jawType;

      // Handle regular occlusion
      if (pontic.occlusion) {
        ponticObject.occlusionDistances = await getOcclusionDistancesFromFilename(pontic.occlusion, occlusions, occlusionsConfig || undefined);
      }

      // Handle jaw displacement occlusion
      if (stageJawDisplacement?.occlusions) {
        const jdOcclusion = stageJawDisplacement.occlusions.find((occ: IToothlikeOcclusion) => occ.toothlikeId === pontic.id);
        if (jdOcclusion) {
          ponticObject.jdOcclusionDistances = await getOcclusionDistancesFromFilename(jdOcclusion.occlusion, occlusions, occlusionsConfig || undefined);
        }
      }

      (ponticObject as PonticObjectDescription).toothId = pontic.number;
    });
  }

  if (jaw.iprs) {
    jaw.iprs
      .forEach((ipr: IIPR, index: number) => {
        if ((ipr.appliedOnStage + 1) <= parseInt(stageId, 10)) {
          const firstToothName = getNameByFile(fileNames[ipr.adjacentTeethFiles[0]]);
          const secondToothName = getNameByFile(fileNames[ipr.adjacentTeethFiles[1]]);

          const iprName = `ipr-${jawType === JawType.Upper ? 'u': 'l'}-${stageId}-${index + 1}`;
          const iprObject = objects[iprName] as IPRObjectDescription;

          iprObject.transform = cloneDeep(objects[firstToothName].transform);
          iprObject.jaw = jawType;
          iprObject.visible = true;
          iprObject.firstToothName = firstToothName;
          iprObject.secondToothName = secondToothName;
        }
      });
  }
};

export const getObjectsForStage = async (
  stage: IStage,
  allObjects: Dictionary<ObjectDescription>,
  fileNames: string[],
  occlusions: RawFile[],
  configProvider?: ConfigProvider,
  cbct?: ICbct,
): Promise<ObjectDescription[]> => {
  const stageObjects = cloneDeep(allObjects);
  if (stage.enabled && stage.upper && stage.lower) {
    await fillJawObjects(stage.upper, stageObjects, JawType.Upper, stage.id, fileNames, occlusions, configProvider, cbct, stage.jawDisplacement);
    await fillJawObjects(stage.lower, stageObjects, JawType.Lower, stage.id, fileNames, occlusions, configProvider, cbct, stage.jawDisplacement);
  }

  return Object.values(stageObjects);
};
